import type { Chart, Plugin } from 'chart.js'
import type { ReleaseBand } from '../../lib/types'

const RELEASE_COLORS = [
  '#2563eb',
  '#16a34a',
  '#d97706',
  '#7c3aed',
  '#0891b2',
  '#be185d',
  '#4d7c0f',
  '#9333ea',
]

interface ReleaseBandWithColor extends ReleaseBand {
  color: string
}

export function assignReleaseBandColors(
  releaseBands: ReleaseBand[] = []
): ReleaseBandWithColor[] {
  return releaseBands.map((band, index) => ({
    ...band,
    color: RELEASE_COLORS[index % RELEASE_COLORS.length],
  }))
}

export function createReleaseBandsPlugin(
  periods: string[],
  releaseBands: ReleaseBand[] = []
): Plugin<'line'> {
  const coloredBands = assignReleaseBandColors(releaseBands)

  return {
    id: 'releaseBands',
    beforeDatasetsDraw(chart) {
      if (!periods.length || !coloredBands.length) {
        return
      }

      const { ctx, chartArea } = chart
      if (!chartArea) {
        return
      }

      ctx.save()

      coloredBands.forEach((band) => {
        const left = getStartBoundaryPixel(chart, periods, band.start)
        const right = getEndBoundaryPixel(chart, periods, band.end)

        if (left === null || right === null || right <= left) {
          return
        }

        ctx.fillStyle = withOpacity(band.color, 0.08)
        ctx.fillRect(left, chartArea.top, right - left, chartArea.bottom - chartArea.top)

        if (left > chartArea.left + 1) {
          ctx.strokeStyle = withOpacity(band.color, 0.45)
          ctx.lineWidth = 1
          ctx.beginPath()
          ctx.moveTo(left, chartArea.top)
          ctx.lineTo(left, chartArea.bottom)
          ctx.stroke()
        }
      })

      ctx.restore()
    },
  }
}

export function ReleaseBandsLegend({
  releaseBands = [],
}: {
  releaseBands?: ReleaseBand[]
}) {
  const coloredBands = assignReleaseBandColors(releaseBands)
  if (!coloredBands.length) {
    return null
  }

  return (
    <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
      {coloredBands.map((band) => (
        <span key={`${band.release}-${band.start}`} className="inline-flex items-center gap-1.5">
          <span
            aria-hidden="true"
            className="h-2.5 w-2.5 rounded-sm"
            style={{ backgroundColor: withOpacity(band.color, 0.45) }}
          />
          <span>{band.release}</span>
        </span>
      ))}
    </div>
  )
}

function getStartBoundaryPixel(
  chart: Chart<'line'>,
  periods: string[],
  timestamp: string
): number | null {
  const target = Date.parse(timestamp)
  const periodTimes = periods.map((period) => Date.parse(period))

  if (Number.isNaN(target) || periodTimes.some(Number.isNaN)) {
    return null
  }

  const { chartArea } = chart
  if (target <= periodTimes[0]) {
    return chartArea.left
  }

  let bucketIndex = 0
  for (let index = periodTimes.length - 1; index >= 0; index -= 1) {
    if (target >= periodTimes[index]) {
      bucketIndex = index
      break
    }
  }

  if (bucketIndex <= 0) {
    return chartArea.left
  }

  return getMidpointBetweenBuckets(chart, bucketIndex - 1, bucketIndex)
}

function getEndBoundaryPixel(
  chart: Chart<'line'>,
  periods: string[],
  timestamp: string
): number | null {
  const target = Date.parse(timestamp)
  const periodTimes = periods.map((period) => Date.parse(period))

  if (Number.isNaN(target) || periodTimes.some(Number.isNaN)) {
    return null
  }

  const { chartArea } = chart
  if (target >= periodTimes[periodTimes.length - 1]) {
    return chartArea.right
  }

  return getStartBoundaryPixel(chart, periods, timestamp)
}

function getMidpointBetweenBuckets(
  chart: Chart<'line'>,
  leftIndex: number,
  rightIndex: number
): number {
  const xScale = chart.scales.x
  const left = xScale.getPixelForValue(leftIndex)
  const right = xScale.getPixelForValue(rightIndex)
  return left + (right - left) / 2
}

function withOpacity(hex: string, opacity: number): string {
  const normalized = hex.replace('#', '')
  const red = parseInt(normalized.slice(0, 2), 16)
  const green = parseInt(normalized.slice(2, 4), 16)
  const blue = parseInt(normalized.slice(4, 6), 16)

  return `rgba(${red}, ${green}, ${blue}, ${opacity})`
}
